/* ─────────────────────────────────────────────
   PICKER — animal picker + recent devices (Cluster 11)
   ───────────────────────────────────────────── */
import { tokenFor } from '../../shared/token';
import { $, qs, qsa } from './dom';
import { fmtRelative } from './format';
import { showToast } from './toast';
import { ANIMALS, byKey, segAvatarsHtml, deviceDisplayName } from './animals';
import { state } from '../state/app-state';
import { getLeafletMap } from '../state/app-state';
import { MOCK } from '../state/device';
import { colorForToken } from '../state/cohort-model';
import {
  loadDeviceHistory, loadStoredToken, saveToken,
  clearStoredToken, loadActiveSession, loadKeypair,
  clearKeypair, clearActiveSession, forgetFromHistory,
  isPendingDevice,
} from '../storage/persistence';
import { apiGetPings } from '../api/client';
import { drawRoutes } from '../map/tracker';
import { renderSessions, renderDeviceHeader } from './sidebar';
import { stopRecording, updateRecordUi } from './recording';

/* ─────────────────────────────────────────────
   Callbacks injected by main.ts boot() to avoid circular imports
   ───────────────────────────────────────────── */
let _showCb: ((screen: string) => void) | null = null;
export function setPickerShowCb(fn: (screen: string) => void): void { _showCb = fn; }
let _closeDrawerCb: (() => void) | null = null;
export function setPickerCloseDrawerCb(fn: () => void): void { _closeDrawerCb = fn; }
let _ensureRegisteredCb: ((token: string) => Promise<string>) | null = null;
export function setPickerEnsureRegisteredCb(fn: (token: string) => Promise<string>): void { _ensureRegisteredCb = fn; }
let _reconcileCreatedAtCb: ((token: string, created_at: number) => void) | null = null;
export function setPickerReconcileCreatedAtCb(fn: (token: string, created_at: number) => void): void { _reconcileCreatedAtCb = fn; }
let _resetGroupPollLastNowCb: (() => void) | null = null;
export function setPickerResetGroupPollLastNowCb(fn: () => void): void { _resetGroupPollLastNowCb = fn; }
// Fires one immediate cohort poll for the just-activated device (injected
// `pollGroup`). Needed because show('map')'s startCohortLive() short-circuits
// when the 30s poll timer is already running (startGroupPoll is idempotent), so
// an in-place device switch would otherwise leave "By device" empty until the
// next tick. pollGroup self-guards (no device/group → no-op) and re-renders.
let _repollGroupCb: (() => void) | null = null;
export function setPickerRepollGroupCb(fn: () => void): void { _repollGroupCb = fn; }

function renderPicker(){
  const row = $('picker');
  row.innerHTML = ANIMALS.map(a => `
    <button class="animal" data-key="${a.key}" aria-label="${a.name}">
      <img src="${a.img}" alt="${a.name}"/>
      <span class="cap">${a.name}</span>
    </button>
  `).join('');
  qsa('.animal', row).forEach(el => el.addEventListener('click', () => pickAnimal(el.dataset.key, el)));
}
function defaultTarget(){
  // Prefer first empty device slot. If device is full, fall through to first empty group slot.
  const di = state.slots.findIndex(s => s === null);
  if(di >= 0) return {kind:'device', idx: di};
  const gi = state.group.findIndex(s => s === null);
  if(gi >= 0) return {kind:'group', idx: gi};
  return null;
}
function effectiveTarget(){
  if(state.targetOverride){
    const arr = state.targetOverride.kind === 'group' ? state.group : state.slots;
    if(arr[state.targetOverride.idx] === null) return state.targetOverride;
    state.targetOverride = null;
  }
  return defaultTarget();
}
function renderSlots(){
  const target = effectiveTarget();
  // Device slots
  qsa('[data-slot]').forEach(slot => {
    const i = +slot.dataset.slot;
    const filled = state.slots[i];
    slot.classList.toggle('is-empty', !filled);
    slot.classList.toggle('is-filled', !!filled);
    slot.classList.toggle('is-target', !filled && !!target && target.kind==='device' && target.idx===i);
    if(filled){
      slot.innerHTML = `<span class="slot-index">${['i','ii','iii'][i]}</span><img src="${byKey[filled].img}" alt="${byKey[filled].name}"/>`;
    } else {
      slot.innerHTML = `<span class="slot-index">${['i','ii','iii'][i]}</span><span class="slot-q">?</span>`;
    }
    slot.onclick = () => {
      if(state.slots[i]) state.slots[i] = null;
      state.targetOverride = {kind:'device', idx:i};
      renderSlots();
    };
  });
  // Group slots
  qsa('[data-gslot]').forEach(slot => {
    const i = +slot.dataset.gslot;
    const filled = state.group[i];
    slot.classList.toggle('is-empty', !filled);
    slot.classList.toggle('is-filled', !!filled);
    slot.classList.toggle('is-target', !filled && !!target && target.kind==='group' && target.idx===i);
    const lbl = ['g·i','g·ii'][i];
    if(filled){
      slot.innerHTML = `<span class="slot-index">${lbl}</span><img src="${byKey[filled].img}" alt="${byKey[filled].name}"/>`;
    } else {
      slot.innerHTML = `<span class="slot-index">${lbl}</span><span class="slot-q">?</span>`;
    }
    slot.onclick = () => {
      if(state.group[i]) state.group[i] = null;
      state.targetOverride = {kind:'group', idx:i};
      renderSlots();
    };
  });
  renderCombo();
}
function renderCombo(){
  const filled = state.slots.filter(Boolean);
  const combo = $('combo');
  const cta = $('create-cta');
  if(filled.length === 3){
    combo.classList.add('is-visible');
    const names = state.slots.map(k => byKey[k as string].name);
    const groupSet = state.group.every(Boolean);
    const gHtml = groupSet
      ? `<span class="name-group">${state.group.map(k => byKey[k as string].name).join(' · ')}</span><span class="name-slash">/</span>`
      : '';
    $('combo-name').innerHTML = gHtml + names.map((n: string, i: number)=> i===1 ? `<em>${n}</em>` : n).join(' · ');
    $('combo-token').textContent = tokenFor(state.slots as string[], groupSet ? state.group as string[] : null);
    cta.disabled = false;
  } else {
    combo.classList.remove('is-visible');
    $('combo-name').textContent = '';
    $('combo-token').textContent = '';
    cta.disabled = true;
  }
}
function pickAnimal(key: string, srcEl: any){
  const target = effectiveTarget();
  if(!target) return;
  const img = qs('img', srcEl);
  img.animate(
    [{transform:'scale(1)'},{transform:'scale(1.16)'},{transform:'scale(1)'}],
    {duration:280, easing:'cubic-bezier(0.2,0.6,0.2,1)'}
  );
  const slotEl = target.kind === 'group'
    ? qs(`.slot[data-gslot="${target.idx}"]`)
    : qs(`.slot[data-slot="${target.idx}"]`);
  const from = img.getBoundingClientRect();
  const to   = slotEl.getBoundingClientRect();
  const ghost = document.createElement('img');
  ghost.src = byKey[key].img; ghost.className = 'ghost';
  ghost.style.left = from.left + 'px';
  ghost.style.top  = from.top  + 'px';
  ghost.style.transform = 'translate(0,0) scale(1)';
  document.body.appendChild(ghost);
  requestAnimationFrame(()=>{
    const dx = (to.left + to.width/2) - (from.left + from.width/2);
    const dy = (to.top + to.height/2) - (from.top + from.height/2);
    ghost.style.transform = `translate(${dx}px, ${dy - 14}px) scale(1.05) rotate(-6deg)`;
    ghost.style.opacity = '0';
  });
  setTimeout(()=> ghost.remove(), 380);
  setTimeout(()=>{
    if(target.kind === 'device') state.slots[target.idx] = key;
    else state.group[target.idx] = key;
    // advance override if still applicable (next empty within same category)
    if(state.targetOverride){
      const arr = state.targetOverride.kind === 'group' ? state.group : state.slots;
      const nextIdx = arr.findIndex(s => s === null);
      state.targetOverride = nextIdx >= 0 ? {kind: state.targetOverride.kind, idx: nextIdx} : null;
    }
    renderSlots();
  }, 240);
}

// Activate a device — fresh or restored — and route to the map. The single
// path shared by the create CTA and the Recent-devices list, so the two
// can't drift. A restored device resumes its persisted session and hydrates
// its pings from D1; a fresh one starts clean. Expects
// `{ token, animals, group, created_at? }`.
function activateDevice(device: any, restored: boolean){
  const { token, animals, group } = device;
  const created_at = device.created_at || Date.now();
  saveToken(token);
  MOCK.device = { animals, group, token, created: new Date(created_at).toISOString() };
  MOCK.pings = [];
  state.cohort.clear();
  _resetGroupPollLastNowCb?.();
  // If this device was minted offline on a prior visit, try to register it
  // now (no-op for normally-created devices). Fire-and-forget.
  _ensureRegisteredCb?.(token);
  // Restoring an existing device may have a persisted open session; pick it
  // up so future pings carry the right session_id. Fresh creates have no LS
  // entry, so this stays null for them.
  state.activeSession = restored ? loadActiveSession(token) : null;
  // For a restored device, hydrate pings asynchronously (the screen
  // transition can proceed in parallel; pings land on the map a moment
  // later via drawRoutes).
  if(restored){
    apiGetPings(token).then(res => {
      MOCK.pings = res.pings;
      _reconcileCreatedAtCb?.(token, res.created_at);
      if(MOCK.device && MOCK.device.token === token) MOCK.device.tag = res.tag;
      if(getLeafletMap()) drawRoutes({fit: true});
      renderSessions();
      renderDeviceHeader();
      updateRecordUi();
    }).catch(err => console.warn('[locus] restore-pings failed', err));
  }
  _showCb?.('map');
  // In-place device switch while "By device" is the active view: the cohort was
  // just cleared (line above), but show('map') → startCohortLive() is a no-op
  // when the poll timer is already running, so it would sit empty until the next
  // 30s tick. Fire one immediate poll for the NEW device so the cohort list + map
  // repaint now (the user must otherwise toggle tabs to force a refresh).
  if(state.viewMode === 'devices') _repollGroupCb?.();
}

// Fade the picker out, then run `after` (the navigation). Kept separate from
// activateDevice so a Recent-devices tap can restore instantly without it.
function animatePickerOut(after: () => void){
  const inner = qs('.create-inner');
  const anim = inner.animate(
    [{opacity:1, transform:'none'},{opacity:0, transform:'translateY(-14px)'}],
    {duration:360, easing:'cubic-bezier(0.2,0.6,0.2,1)', fill:'forwards'}
  );
  setTimeout(()=>{
    // WAAPI animations with fill:'forwards' keep their end keyframe applied
    // until the Animation is cancelled — clearing inline styles alone won't
    // lift them. Cancel before navigating so a subsequent return to #/create
    // (forget device / open another device) shows the picker at full opacity.
    anim.cancel();
    after();
  }, 360);
}

/* ─────────────────────────────────────────────
   RECENT DEVICES (picker)
   ───────────────────────────────────────────── */
// Surface this browser's previously-created devices so they can be re-picked
// in one tap, instead of relying on re-typing the same animals. The picker's
// "recent devices" button opens a modal whose body this fills.
// Local-only: every entry was created in this browser and holds its own key.
// Cross-device/browser key transfer is the ownership-gated zero-knowledge
// backup (ADR 0016): an owner can encrypt a claimed device's identity with a
// passphrase and store the ciphertext on their account (Device panel → "Back
// up this identity"), then restore it elsewhere (Account → "Restore a device
// from backup"). The plaintext seed/passphrase never leave the browser.
//
// Show/hide the picker's "recent devices" button based on whether any history
// exists (the modal is pointless with nothing to list).
function refreshRecentDevicesButton(){
  const btn = $('open-recent');
  if(!btn) return;
  btn.style.display = loadDeviceHistory().length ? '' : 'none';
}

function renderRecentDevices(){
  const box = $('recent-devices');
  if(!box) return;
  const cur = loadStoredToken();
  const items = loadDeviceHistory()
    .slice()
    .sort((a: any, b: any) => (b.created_at || 0) - (a.created_at || 0));
  if(items.length === 0){
    box.innerHTML = `<div class="rd-empty">No devices created in this browser yet.</div>`;
    return;
  }
  const rows = items.map((d: any) => {
    const isCur = d.token === cur;
    // Restorable only with the signing key on hand (or still pending a mint).
    const usable = isCur || isPendingDevice(d.token) || !!loadKeypair(d.token);
    const swatch = isCur ? '#B84A22' : colorForToken(d.token);
    const hasGroup = !!(d.group && d.group.length === 2);
    // Group animals stack on their own row ABOVE the three device animals, so
    // a grouped device's five avatars don't overflow the narrow modal. A
    // high-entropy device has no art → a single colour swatch (segAvatarsHtml).
    const groupRow = hasGroup ? `<div class="rd-trio-row rd-trio-group">${segAvatarsHtml(d.group, swatch)}</div>` : '';
    const deviceRow = `<div class="rd-trio-row">${segAvatarsHtml(d.animals, swatch)}</div>`;
    // Name written out on one horizontal line below the avatars. F3: reuse the
    // `items` history already parsed above rather than re-parsing per row.
    const names = deviceDisplayName(d.token, d.animals, d.group, items);
    const tail = d.token.split('-').slice(-1)[0];
    const ago = d.created_at ? fmtRelative(d.created_at) : '—';
    return `<div class="rd-row ${isCur?'is-active':''} ${usable?'':'is-stale'}" data-token="${d.token}">
      <div class="rd-row-head">
        <div class="rd-trio" style="border-left:3px solid ${swatch}">${groupRow}${deviceRow}</div>
        <div class="rd-actions">
          <button class="rd-use" data-token="${d.token}" ${(isCur||!usable)?'disabled':''}>${isCur?'In use':'Use'}</button>
          <button class="rd-forget" data-token="${d.token}" aria-label="Forget device">Forget</button>
        </div>
      </div>
      <div class="rd-name">${names}${isCur?'<span class="dr-cur">current</span>':''}${d.pending?'<span class="rd-pending">offline</span>':''}</div>
      <div class="rd-meta">${tail} · ${ago}</div>
    </div>`;
  }).join('');
  box.innerHTML = `<div class="rd-list">${rows}</div>`;
  // Whole row (and the Use button) restores; Forget is isolated.
  qsa('.rd-row', box).forEach((r: any) => r.onclick = () => useRecentDevice(r.dataset.token));
  qsa('.rd-forget', box).forEach((b: any) => b.addEventListener('click', (e: any) => {
    e.stopPropagation();
    forgetRecentDevice(b.dataset.token);
  }));
}

function useRecentDevice(token: string){
  _closeDrawerCb?.();
  // Already the active device — just return to its map.
  if(token === loadStoredToken()){ _showCb?.('map'); return; }
  const entry = loadDeviceHistory().find((d: any)=> d.token === token);
  if(!entry) return;
  if(!(loadKeypair(token) || isPendingDevice(token))){
    showToast('this device has no signing key in this browser');
    return;
  }
  activateDevice(entry, /*restored*/ true);
}

function forgetRecentDevice(token: string){
  // No window.confirm() guard: it is suppressed in standalone/PWA contexts,
  // which silently no-oped the whole action (the reported bug). Forget is
  // local-only (V1 clears local state; the server row persists), so it proceeds
  // unconditionally and the toast below is the feedback. Mirrors the 1e8bbcd
  // revokeCurrentGroupClaim fix; if a confirm is wanted later use a <dialog>.
  const wasCurrent = token === loadStoredToken();
  forgetFromHistory(token);
  clearKeypair(token);
  clearActiveSession(token);
  if(wasCurrent){
    // Tear down the active device too (mirrors the menu "Forget this device").
    clearStoredToken();
    if(state.recording) stopRecording();
    MOCK.device = null;
    state.activeSession = null;
    state.cohort.clear();
    MOCK.pings = [];
  }
  renderRecentDevices();
  refreshRecentDevicesButton();
  showToast('device forgotten');
}

// A genuinely fresh device: clear the picker and route to it.
function newDevice(){
  state.slots = [null, null, null];
  state.group = [null, null];
  state.targetOverride = null;
  renderSlots();
  _showCb?.('create');
}

// Edit the current device's identifier (prefill the picker with its animals/
// group). Reachable from the "By device" empty-state "Edit identifier" CTA.
function editCurrentDevice(){
  if(MOCK.device){
    state.slots = MOCK.device.animals.slice();
    state.group = MOCK.device.group ? MOCK.device.group.slice() : [null, null];
  }
  state.targetOverride = null;
  renderSlots();
  _closeDrawerCb?.();
  _showCb?.('create');
}

export {
  renderPicker,
  renderSlots,
  renderCombo,
  pickAnimal,
  activateDevice,
  animatePickerOut,
  renderRecentDevices,
  useRecentDevice,
  forgetRecentDevice,
  refreshRecentDevicesButton,
  defaultTarget,
  effectiveTarget,
  newDevice,
  editCurrentDevice,
};
